import { useState, type ReactNode } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { T, scoreColor } from "../../tokens";
import { useAuthStore } from "../../store";

// ── Types ─────────────────────────────────────────────────────────────────

type UserDetailData = {
  user: {
    id: string; name: string; email: string;
    segment: string | null; emailVerified: boolean;
    isActive: boolean; createdAt: string; updatedAt: string;
    /** Partner attribution (e.g. 'exh1b') when provisioned via the Partner API; null for direct signups. */
    partnerSource: string | null;
  };
  resume: {
    id: string; seniorityLevel: string | null;
    targetRoles: string[]; targetLocations: string[];
    workAuthTags: string[]; salaryMinLpa: number | null; salaryMaxLpa: number | null;
    createdAt: string; updatedAt: string;
    parsed: {
      seniority?: string; totalYears?: number; skills: string[];
      domains: string[]; location?: string; summary?: string;
    } | null;
  } | null;
  matchSummary: { total: number; llmScored: number; autoApplyEligible: number; stale: number };
  topMatches: MatchRow[];
  applicationSummary: Record<string, number>;
};

type MatchRow = {
  id: string; jobId: string; jobTitle: string; company: string;
  location: string | null; weightedScore: number | null; semanticScore: number | null;
  modelUsed: string | null; autoApplyEligible: boolean; isStale: boolean;
  staleReason?: string | null; scoredAt: string; explanation: string;
};

type AppRow = {
  id: string; jobId: string; jobTitle: string; company: string;
  status: string; appliedAt: string | null; retryCount: number;
  notes: string | null; createdAt: string;
};

type Paginated<T> = { items: T[]; total: number; page: number; limit: number };

// ── Helpers ───────────────────────────────────────────────────────────────

function fmtDate(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
}

function TagChip({ label }: { label: string }) {
  return (
    <span style={{ display: "inline-block", padding: "2px 7px",
      background: T.s3, border: `1px solid ${T.b1}`,
      borderRadius: "4px", fontFamily: T.mono, fontSize: "10px", color: T.t2, marginRight: "4px", marginBottom: "4px" }}>
      {label}
    </span>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
      padding: "7px 0", borderBottom: `1px solid ${T.b0}`, gap: "12px" }}>
      <span style={{ fontFamily: T.body, fontSize: "11px", color: T.t3, flexShrink: 0 }}>{label}</span>
      <span style={{ fontFamily: T.mono, fontSize: "11px", color: T.t1, textAlign: "right" }}>{value}</span>
    </div>
  );
}

// ── Account block (view + internal-admin edit of name/email/segment) ─────────

// Mirrors the shared user_segment enum (packages/shared schema). Edit-only options.
const USER_SEGMENTS = [
  "india-global-remote", "return-to-india", "senior-non-it",
  "freshly-returned", "diaspora", "senior-freelancer",
] as const;

function AccountBlock({ user }: { user: UserDetailData["user"] }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [segment, setSegment] = useState(user.segment ?? USER_SEGMENTS[0]);

  const startEdit = () => {
    setName(user.name); setEmail(user.email); setSegment(user.segment ?? USER_SEGMENTS[0]);
    setEditing(true);
  };

  const save = useMutation({
    mutationFn: async () => {
      const { data } = await axios.patch(`/api/admin/users/${user.id}`, { name, email, segment });
      return data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-user-detail", user.id] }); setEditing(false); },
  });

  const err = save.error as { response?: { status?: number; data?: { error?: string } } } | null;
  const errMsg = err?.response?.status === 409
    ? "Email already in use"
    : err?.response?.data?.error || (save.isError ? "Save failed" : "");

  // Admin-triggered password reset (email flow) — the admin never sets a password.
  const reset = useMutation({
    mutationFn: async () => {
      const { data } = await axios.post<{ reset: string; emailSent: boolean }>(`/api/admin/users/${user.id}/reset-password`, {});
      return data;
    },
  });
  const resetMsg = reset.isError
    ? "Reset failed"
    : reset.data
      ? reset.data.reset === "no_password_account"
        ? "No password account (Google/partner user)"
        : reset.data.emailSent ? "Reset email sent" : "Token set (email disabled)"
      : "";

  // Soft-delete: deactivate (reversible) / reactivate.
  const activate = useMutation({
    mutationFn: async (next: boolean) => {
      const action = next ? "reactivate" : "deactivate";
      const { data } = await axios.post(`/api/admin/users/${user.id}/${action}`, {});
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-user-detail", user.id] }),
  });

  const inputStyle = {
    width: "100%", boxSizing: "border-box" as const, padding: "6px 8px",
    background: T.s1, border: `1px solid ${T.b1}`, borderRadius: "5px",
    color: T.t0, fontFamily: T.body, fontSize: "12px", outline: "none",
  };
  const fieldRow = (label: string, node: ReactNode) => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
      <span style={{ fontFamily: T.body, fontSize: "11px", color: T.t3, width: "72px", flexShrink: 0 }}>{label}</span>
      <div style={{ flex: 1 }}>{node}</div>
    </div>
  );

  return (
    <div style={{ background: T.s2, border: `1px solid ${T.b0}`, borderRadius: "8px",
      padding: "14px 16px", marginBottom: "12px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
        <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
          letterSpacing: "0.10em", textTransform: "uppercase" }}>Account</div>
        {!editing && (
          <button type="button" onClick={startEdit}
            style={{ padding: "3px 10px", background: "transparent", border: `1px solid ${T.b1}`,
              borderRadius: "5px", color: T.t2, fontFamily: T.mono, fontSize: "10px", cursor: "pointer" }}>
            Edit
          </button>
        )}
      </div>

      {editing ? (
        <>
          {fieldRow("Name", <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />)}
          {fieldRow("Email", <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" style={inputStyle} />)}
          {fieldRow("Segment", (
            <select value={segment ?? undefined} onChange={(e) => setSegment(e.target.value)} style={inputStyle}>
              {USER_SEGMENTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          ))}
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "10px", marginTop: "10px" }}>
            {errMsg && <span style={{ fontFamily: T.body, fontSize: "11px", color: T.err }}>{errMsg}</span>}
            <button type="button" onClick={() => { setEditing(false); save.reset(); }} disabled={save.isPending}
              style={{ padding: "6px 14px", background: "transparent", border: `1px solid ${T.b1}`,
                borderRadius: "6px", color: T.t2, fontFamily: T.body, fontSize: "12px", cursor: "pointer" }}>
              Cancel
            </button>
            <button type="button" onClick={() => save.mutate()} disabled={save.isPending || !name.trim() || !email.trim()}
              style={{ padding: "6px 16px", background: T.teal, border: "none", borderRadius: "6px",
                color: "#fff", fontFamily: T.body, fontSize: "12px", fontWeight: 600,
                cursor: save.isPending ? "default" : "pointer", opacity: save.isPending ? 0.6 : 1 }}>
              {save.isPending ? "Saving…" : "Save"}
            </button>
          </div>
        </>
      ) : (
        <>
          <Row label="Name"     value={user.name} />
          <Row label="Email"    value={user.email} />
          <Row label="Source"   value={user.partnerSource ? user.partnerSource.toUpperCase() : "Direct"} />
          <Row label="Segment"  value={user.segment ?? "—"} />
          <Row label="Verified" value={user.emailVerified ? "Yes" : "No"} />
          <Row label="Active"   value={user.isActive ? "Yes" : "No"} />
          <Row label="Joined"   value={fmtDate(user.createdAt)} />

          {/* Security — admin-triggered password reset (email flow) */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
            gap: "10px", paddingTop: "10px", marginTop: "6px", borderTop: `1px solid ${T.b0}` }}>
            <span style={{ fontFamily: T.body, fontSize: "11px", color: resetMsg ? (reset.isError ? T.err : T.ok) : T.t3 }}>
              {resetMsg || "Send a password-reset link to this user"}
            </span>
            <div style={{ display: "flex", gap: "8px", flexShrink: 0 }}>
              <button type="button"
                onClick={() => {
                  const next = !user.isActive;
                  if (next || confirm("Deactivate this user? They will be logged out and cannot sign in until reactivated.")) activate.mutate(next);
                }}
                disabled={activate.isPending}
                style={{ padding: "5px 12px", background: "transparent",
                  border: `1px solid ${user.isActive ? T.err : T.ok}`, borderRadius: "5px",
                  color: user.isActive ? T.err : T.ok, fontFamily: T.mono, fontSize: "10px",
                  cursor: activate.isPending ? "default" : "pointer", opacity: activate.isPending ? 0.6 : 1 }}>
                {activate.isPending ? "…" : user.isActive ? "Deactivate" : "Reactivate"}
              </button>
              <button type="button" onClick={() => reset.mutate()} disabled={reset.isPending}
                style={{ padding: "5px 12px", background: "transparent", border: `1px solid ${T.b1}`,
                  borderRadius: "5px", color: T.t2, fontFamily: T.mono, fontSize: "10px",
                  cursor: reset.isPending ? "default" : "pointer", opacity: reset.isPending ? 0.6 : 1 }}>
                {reset.isPending ? "Sending…" : "Send reset email"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Résumé admin controls (internal-admin upload / remove) ───────────────────

function ResumeAdminControls({ userId, hasResume }: { userId: string; hasResume: boolean }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("no file");
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`/api/admin/users/${userId}/resume`, fd);
      return data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-user-detail", userId] }); setFile(null); },
  });
  const remove = useMutation({
    mutationFn: async () => { const { data } = await axios.delete(`/api/admin/users/${userId}/resume`); return data; },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-user-detail", userId] }),
  });

  const busy = upload.isPending || remove.isPending;
  const msg = upload.isError ? "Upload failed (parse/format)" : remove.isError ? "Remove failed"
    : upload.isSuccess ? "Resume uploaded" : remove.isSuccess ? "Resume removed" : "";

  return (
    <div style={{ background: T.s2, border: `1px solid ${T.b0}`, borderRadius: "8px",
      padding: "12px 16px", marginBottom: "12px" }}>
      <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
        letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "8px" }}>Resume — Admin Actions</div>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
        <input type="file" accept=".pdf,.docx,application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          style={{ fontFamily: T.body, fontSize: "11px", color: T.t2, flex: "1 1 160px" }} />
        <button type="button" onClick={() => upload.mutate()} disabled={busy || !file}
          style={{ padding: "6px 14px", background: T.teal, border: "none", borderRadius: "6px",
            color: "#fff", fontFamily: T.body, fontSize: "12px", fontWeight: 600,
            cursor: busy || !file ? "default" : "pointer", opacity: busy || !file ? 0.6 : 1 }}>
          {upload.isPending ? "Parsing…" : "Upload"}
        </button>
        {hasResume && (
          <button type="button" onClick={() => { if (confirm("Remove this user's active resume and its matches?")) remove.mutate(); }} disabled={busy}
            style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${T.err}`,
              borderRadius: "6px", color: T.err, fontFamily: T.body, fontSize: "12px",
              cursor: busy ? "default" : "pointer", opacity: busy ? 0.6 : 1 }}>
            {remove.isPending ? "Removing…" : "Remove"}
          </button>
        )}
      </div>
      {msg && <div style={{ marginTop: "8px", fontFamily: T.mono, fontSize: "10px",
        color: (upload.isError || remove.isError) ? T.err : T.ok }}>{msg}</div>}
      <div style={{ marginTop: "6px", fontFamily: T.body, fontSize: "10px", color: T.t3 }}>
        Uploading parses + embeds the résumé (marked admin-uploaded) and refreshes matches.
      </div>
    </div>
  );
}

// ── Profile tab ───────────────────────────────────────────────────────────

function ProfileTab({ data }: { data: UserDetailData }) {
  const { user, resume, matchSummary, applicationSummary } = data;
  const totalApps = Object.values(applicationSummary).reduce((a, b) => a + b, 0);

  return (
    <div>
      {/* Account (view + internal-admin edit) */}
      <AccountBlock user={user} />

      {/* Match summary */}
      <div style={{ background: T.s2, border: `1px solid ${T.b0}`, borderRadius: "8px",
        padding: "14px 16px", marginBottom: "12px" }}>
        <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
          letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "8px" }}>Match Summary</div>
        <Row label="Total matches"    value={matchSummary.total} />
        <Row label="LLM scored"       value={matchSummary.llmScored} />
        <Row label="Auto-apply elig." value={matchSummary.autoApplyEligible} />
        <Row label="Stale"            value={matchSummary.stale} />
        <Row label="Applications"     value={totalApps} />
      </div>

      {/* Resume */}
      {resume ? (
        <div style={{ background: T.s2, border: `1px solid ${T.b0}`, borderRadius: "8px",
          padding: "14px 16px", marginBottom: "12px" }}>
          <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
            letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "8px" }}>Active Resume</div>
          <Row label="Seniority" value={resume.seniorityLevel ?? resume.parsed?.seniority ?? "—"} />
          <Row label="Exp (yrs)" value={resume.parsed?.totalYears ?? "—"} />
          <Row label="Salary"    value={
            resume.salaryMinLpa || resume.salaryMaxLpa
              ? `${resume.salaryMinLpa ?? "?"}–${resume.salaryMaxLpa ?? "?"} LPA`
              : "—"
          } />
          <Row label="Location"  value={resume.parsed?.location ?? "—"} />
          <Row label="CV updated" value={fmtDate(resume.updatedAt)} />

          {resume.targetRoles.length > 0 && (
            <div style={{ paddingTop: "8px" }}>
              <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
                letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "6px" }}>Target Roles</div>
              <div>{resume.targetRoles.map((r) => <TagChip key={r} label={r} />)}</div>
            </div>
          )}

          {resume.targetLocations.length > 0 && (
            <div style={{ paddingTop: "6px" }}>
              <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
                letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "6px" }}>Target Locations</div>
              <div>{resume.targetLocations.map((l) => <TagChip key={l} label={l} />)}</div>
            </div>
          )}

          {resume.workAuthTags.length > 0 && (
            <div style={{ paddingTop: "6px" }}>
              <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
                letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "6px" }}>Work Auth</div>
              <div>{resume.workAuthTags.map((t) => <TagChip key={t} label={t} />)}</div>
            </div>
          )}

          {resume.parsed?.skills && resume.parsed.skills.length > 0 && (
            <div style={{ paddingTop: "6px" }}>
              <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
                letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "6px" }}>Skills (top 20)</div>
              <div>{resume.parsed.skills.map((s) => <TagChip key={s} label={s} />)}</div>
            </div>
          )}

          {resume.parsed?.summary && (
            <div style={{ paddingTop: "8px" }}>
              <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
                letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "6px" }}>Summary</div>
              <p style={{ fontFamily: T.body, fontSize: "11px", color: T.t2,
                margin: 0, lineHeight: 1.6 }}>
                {resume.parsed.summary}
              </p>
            </div>
          )}
        </div>
      ) : (
        <div style={{ padding: "12px 16px", background: T.s2, border: `1px solid ${T.b0}`,
          borderRadius: "8px", fontFamily: T.body, fontSize: "12px", color: T.t3, marginBottom: "12px" }}>
          No active resume.
        </div>
      )}

      {/* Admin résumé upload / remove */}
      <ResumeAdminControls userId={user.id} hasResume={!!resume} />
    </div>
  );
}

// ── Matches tab ───────────────────────────────────────────────────────────

function MatchesTab({ userId }: { userId: string }) {
  const [page, setPage] = useState(1);
  const token = useAuthStore((s) => s.token);

  const q = useQuery<Paginated<MatchRow>>({
    queryKey: ["admin-user-matches", userId, page],
    queryFn: async () => {
      const { data } = await axios.get(`/api/admin/users/${userId}/matches`, {
        params: { page, limit: 25 },
      });
      return data;
    },
    enabled: !!token,
    retry: false,
  });

  const totalPages = q.data ? Math.ceil(q.data.total / q.data.limit) : 0;

  if (q.isLoading) return <p style={{ fontFamily: T.body, fontSize: "12px", color: T.t3 }}>Loading…</p>;
  if (q.isError)   return <p style={{ fontFamily: T.body, fontSize: "12px", color: T.err }}>Failed to load.</p>;
  if (!q.data)     return null;

  return (
    <div>
      <div style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3,
        letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "8px" }}>
        {q.data.total} match{q.data.total !== 1 ? "es" : ""}
      </div>

      {q.data.items.map((m) => {
        const score = Math.round(m.weightedScore ?? 0);
        return (
          <div key={m.id} style={{ background: T.s2, border: `1px solid ${T.b0}`,
            borderRadius: "7px", padding: "10px 14px", marginBottom: "8px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "4px" }}>
              <div>
                <div style={{ fontFamily: T.body, fontSize: "13px", color: T.t0, fontWeight: 600 }}>
                  {m.jobTitle}
                </div>
                <div style={{ fontFamily: T.mono, fontSize: "10px", color: T.t2 }}>
                  {m.company}{m.location ? ` · ${m.location}` : ""}
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "3px" }}>
                <span style={{ fontFamily: T.mono, fontSize: "13px", fontWeight: 700,
                  color: scoreColor(score), fontVariantNumeric: "tabular-nums" }}>
                  {score}
                </span>
                {m.isStale && (
                  <span style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3, letterSpacing: "0.08em" }}>stale</span>
                )}
                {m.autoApplyEligible && (
                  <span style={{ fontFamily: T.mono, fontSize: "9px", color: T.ok, letterSpacing: "0.08em" }}>auto✓</span>
                )}
              </div>
            </div>
            {m.explanation && (
              <p style={{ fontFamily: T.body, fontSize: "11px", color: T.t2,
                margin: "6px 0 0", lineHeight: 1.5, borderTop: `1px solid ${T.b0}`, paddingTop: "6px" }}>
                {m.explanation}
              </p>
            )}
            <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3, marginTop: "5px" }}>
              {fmtDate(m.scoredAt)}{m.modelUsed ? ` · ${m.modelUsed}` : ""}
            </div>
          </div>
        );
      })}

      {totalPages > 1 && (
        <div style={{ display: "flex", gap: "8px", alignItems: "center", marginTop: "8px", justifyContent: "flex-end" }}>
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            style={{ padding: "4px 9px", background: T.s3, border: `1px solid ${T.b1}`,
              borderRadius: "5px", color: page <= 1 ? T.t3 : T.t0,
              fontFamily: T.body, fontSize: "11px", cursor: page <= 1 ? "default" : "pointer" }}>‹</button>
          <span style={{ fontFamily: T.mono, fontSize: "10px", color: T.t2 }}>{page} / {totalPages}</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
            style={{ padding: "4px 9px", background: T.s3, border: `1px solid ${T.b1}`,
              borderRadius: "5px", color: page >= totalPages ? T.t3 : T.t0,
              fontFamily: T.body, fontSize: "11px", cursor: page >= totalPages ? "default" : "pointer" }}>›</button>
        </div>
      )}
    </div>
  );
}

// ── Applications tab ──────────────────────────────────────────────────────

const STATUS_COLOR: Record<string, string> = {
  "auto-applied": T.ok, "applied": T.ok, "interviewing": T.tealSoft,
  "offer": "#A78BFA", "rejected": T.err, "blocked": T.err,
  "pending-review": T.warn, "saved": T.info, "skipped": T.t3,
};

function ApplicationsTab({ userId, summary }: { userId: string; summary: Record<string, number> }) {
  const [page, setPage] = useState(1);
  const token = useAuthStore((s) => s.token);

  const q = useQuery<Paginated<AppRow>>({
    queryKey: ["admin-user-applications", userId, page],
    queryFn: async () => {
      const { data } = await axios.get(`/api/admin/users/${userId}/applications`, {
        params: { page, limit: 25 },
      });
      return data;
    },
    enabled: !!token,
    retry: false,
  });

  const totalPages = q.data ? Math.ceil(q.data.total / q.data.limit) : 0;

  const summaryEntries = Object.entries(summary).filter(([, v]) => v > 0)
    .sort(([, a], [, b]) => b - a);

  return (
    <div>
      {/* Status summary chips */}
      {summaryEntries.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "12px" }}>
          {summaryEntries.map(([status, count]) => (
            <span key={status} style={{
              padding: "3px 9px", borderRadius: "4px",
              background: T.s3, border: `1px solid ${T.b1}`,
              fontFamily: T.mono, fontSize: "10px",
              color: STATUS_COLOR[status] ?? T.t2,
            }}>
              {status} · {count}
            </span>
          ))}
        </div>
      )}

      {q.isLoading && <p style={{ fontFamily: T.body, fontSize: "12px", color: T.t3 }}>Loading…</p>}
      {q.isError   && <p style={{ fontFamily: T.body, fontSize: "12px", color: T.err }}>Failed to load.</p>}

      {q.data?.items.map((a) => (
        <div key={a.id} style={{ background: T.s2, border: `1px solid ${T.b0}`,
          borderRadius: "7px", padding: "10px 14px", marginBottom: "8px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontFamily: T.body, fontSize: "13px", color: T.t0, fontWeight: 600 }}>
                {a.jobTitle}
              </div>
              <div style={{ fontFamily: T.mono, fontSize: "10px", color: T.t2 }}>{a.company}</div>
            </div>
            <span style={{
              fontFamily: T.mono, fontSize: "10px", letterSpacing: "0.06em",
              color: STATUS_COLOR[a.status] ?? T.t2,
            }}>
              {a.status}
            </span>
          </div>
          <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3, marginTop: "5px" }}>
            Created {fmtDate(a.createdAt)}
            {a.appliedAt ? ` · Applied ${fmtDate(a.appliedAt)}` : ""}
            {a.retryCount > 0 ? ` · ${a.retryCount} retry` : ""}
          </div>
          {a.notes && (
            <p style={{ fontFamily: T.body, fontSize: "11px", color: T.t2,
              margin: "5px 0 0", lineHeight: 1.5 }}>
              {a.notes}
            </p>
          )}
        </div>
      ))}

      {q.data && totalPages > 1 && (
        <div style={{ display: "flex", gap: "8px", alignItems: "center", marginTop: "8px", justifyContent: "flex-end" }}>
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            style={{ padding: "4px 9px", background: T.s3, border: `1px solid ${T.b1}`,
              borderRadius: "5px", color: page <= 1 ? T.t3 : T.t0,
              fontFamily: T.body, fontSize: "11px", cursor: page <= 1 ? "default" : "pointer" }}>‹</button>
          <span style={{ fontFamily: T.mono, fontSize: "10px", color: T.t2 }}>{page} / {totalPages}</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
            style={{ padding: "4px 9px", background: T.s3, border: `1px solid ${T.b1}`,
              borderRadius: "5px", color: page >= totalPages ? T.t3 : T.t0,
              fontFamily: T.body, fontSize: "11px", cursor: page >= totalPages ? "default" : "pointer" }}>›</button>
        </div>
      )}
    </div>
  );
}

// ── JD Pool Fit tab ───────────────────────────────────────────────────────

type JdPoolData = {
  activeJobs: number;
  matchedJobs: number;
  relevantJobs: number;
  needsJdCount: number;
  hasTargetRoles: boolean;
  targetRoles: string[];
  hasRoleGap: boolean;
  topSources: { source: string; count: number }[];
};

function PoolFitTab({ userId }: { userId: string }) {
  const token = useAuthStore((s) => s.token);

  const q = useQuery<JdPoolData>({
    queryKey: ["admin-user-jd-pool", userId],
    queryFn: async () => {
      const { data } = await axios.get(`/api/admin/users/${userId}/jd-pool`);
      return data;
    },
    enabled: !!token,
    retry: false,
  });

  if (q.isLoading) return <p style={{ fontFamily: T.body, fontSize: "12px", color: T.t3 }}>Loading…</p>;
  if (q.isError)   return <p style={{ fontFamily: T.body, fontSize: "12px", color: T.err }}>Failed to load.</p>;
  if (!q.data)     return null;

  const d = q.data;
  const matchPct = d.activeJobs > 0 ? Math.round((d.matchedJobs / d.activeJobs) * 100) : 0;

  return (
    <div>
      {/* Role gap warning */}
      {d.hasRoleGap && (
        <div style={{ background: T.warnBg, border: `1px solid ${T.warnBdr}`,
          borderRadius: "7px", padding: "10px 14px", marginBottom: "12px" }}>
          <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.warn,
            letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "4px" }}>Role gap</div>
          <p style={{ fontFamily: T.body, fontSize: "12px", color: T.warn, margin: 0, lineHeight: 1.5 }}>
            No active jobs match this user's target roles. Consider updating target roles or expanding job sources.
          </p>
        </div>
      )}

      {!d.hasTargetRoles && (
        <div style={{ background: T.s3, border: `1px solid ${T.b1}`,
          borderRadius: "7px", padding: "10px 14px", marginBottom: "12px" }}>
          <p style={{ fontFamily: T.body, fontSize: "12px", color: T.t2, margin: 0 }}>
            No target roles configured — relevance matching unavailable.
          </p>
        </div>
      )}

      {/* Pool counters */}
      <div style={{ background: T.s2, border: `1px solid ${T.b0}`,
        borderRadius: "8px", padding: "14px 16px", marginBottom: "12px" }}>
        <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
          letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "8px" }}>JD Pool</div>
        <Row label="Active jobs in pool" value={d.activeJobs} />
        {d.hasTargetRoles && (
          <Row label="Role-relevant jobs" value={
            <span style={{ color: d.relevantJobs > 0 ? T.ok : T.err,
              fontFamily: T.mono, fontSize: "11px" }}>
              {d.relevantJobs}
            </span>
          } />
        )}
        <Row label="Already matched"     value={`${d.matchedJobs} (${matchPct}%)`} />
        <Row label="Needs JD enrichment" value={
          <span style={{ color: d.needsJdCount > 0 ? T.warn : T.t2,
            fontFamily: T.mono, fontSize: "11px" }}>
            {d.needsJdCount}
          </span>
        } />
      </div>

      {/* Top sources */}
      {d.topSources.length > 0 && (
        <div style={{ background: T.s2, border: `1px solid ${T.b0}`,
          borderRadius: "8px", padding: "14px 16px" }}>
          <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
            letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "10px" }}>Job Sources</div>
          {d.topSources.map(({ source, count }) => {
            const pct = d.activeJobs > 0 ? Math.round((count / d.activeJobs) * 100) : 0;
            return (
              <div key={source} style={{ marginBottom: "10px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <span style={{ fontFamily: T.mono, fontSize: "10px", color: T.t2 }}>{source}</span>
                  <span style={{ fontFamily: T.mono, fontSize: "10px", color: T.t1,
                    fontVariantNumeric: "tabular-nums" }}>{count} · {pct}%</span>
                </div>
                <div style={{ height: "3px", background: T.b1, borderRadius: "2px" }}>
                  <div style={{ height: "100%", width: `${pct}%`,
                    background: T.teal, borderRadius: "2px" }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Controls tab ─────────────────────────────────────────────────────────

type Prefs = {
  adminNotes: string | null;
  userMatchingEnabled: boolean;
  resumeMatchingEnabled: boolean;
  disabledReason: string | null;
  disabledAt: string | null;
  updatedAt: string | null;
};

function ControlsTab({ userId }: { userId: string }) {
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();

  const q = useQuery<Prefs>({
    queryKey: ["admin-user-prefs", userId],
    queryFn: async () => {
      const { data } = await axios.get(`/api/admin/users/${userId}/preferences`);
      return data;
    },
    enabled: !!token,
    retry: false,
  });

  const [notes, setNotes]             = useState<string>("");
  const [userEnabled, setUserEnabled] = useState<boolean>(true);
  const [resumeEnabled, setResumeEnabled] = useState<boolean>(true);
  const [reason, setReason]           = useState<string>("");
  const [synced, setSynced]           = useState(false);

  // Sync local state once data loads
  if (q.data && !synced) {
    setNotes(q.data.adminNotes ?? "");
    setUserEnabled(q.data.userMatchingEnabled);
    setResumeEnabled(q.data.resumeMatchingEnabled);
    setReason(q.data.disabledReason ?? "");
    setSynced(true);
  }

  const mut = useMutation({
    mutationFn: async () => {
      const { data } = await axios.patch(`/api/admin/users/${userId}/preferences`, {
        adminNotes:            notes.trim() || null,
        userMatchingEnabled:   userEnabled,
        resumeMatchingEnabled: resumeEnabled,
        disabledReason:        (!userEnabled || !resumeEnabled) ? (reason.trim() || null) : null,
      });
      return data as Prefs;
    },
    onSuccess: (data) => {
      qc.setQueryData(["admin-user-prefs", userId], data);
    },
  });

  const isDisabled = !userEnabled || !resumeEnabled;
  const statusColor = isDisabled ? T.err : T.ok;
  const statusLabel = isDisabled ? "Matching disabled" : "Matching enabled";

  const toggleBtn = (active: boolean, onClick: () => void, onLabel: string, offLabel: string) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "6px 14px", borderRadius: "5px", cursor: "pointer",
        fontFamily: T.mono, fontSize: "10px", letterSpacing: "0.06em",
        background: active ? T.okBg : T.s3,
        border: `1px solid ${active ? T.ok : T.b1}`,
        color: active ? T.ok : T.t3,
        transition: "all 0.15s",
      } as React.CSSProperties}
    >
      {active ? onLabel : offLabel}
    </button>
  );

  if (q.isLoading) return <p style={{ fontFamily: T.body, fontSize: "12px", color: T.t3 }}>Loading…</p>;
  if (q.isError)   return <p style={{ fontFamily: T.body, fontSize: "12px", color: T.err }}>Failed to load.</p>;

  return (
    <div>
      {/* Matching status banner */}
      <div style={{
        background: isDisabled ? T.errBg : T.okBg,
        border: `1px solid ${isDisabled ? T.errBdr : T.ok}`,
        borderRadius: "7px", padding: "10px 14px", marginBottom: "16px",
        display: "flex", alignItems: "center", gap: "8px",
      }}>
        <span style={{ fontFamily: T.mono, fontSize: "11px", color: statusColor, fontWeight: 600 }}>
          {statusLabel}
        </span>
        {q.data?.disabledAt && (
          <span style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3 }}>
            · since {fmtDate(q.data.disabledAt)}
          </span>
        )}
      </div>

      {/* Eligibility toggles */}
      <div style={{ background: T.s2, border: `1px solid ${T.b0}`,
        borderRadius: "8px", padding: "14px 16px", marginBottom: "12px" }}>
        <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
          letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "12px" }}>
          Matching Eligibility
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "8px 0", borderBottom: `1px solid ${T.b0}` }}>
          <span style={{ fontFamily: T.body, fontSize: "12px", color: T.t1 }}>User-level matching</span>
          {toggleBtn(userEnabled, () => setUserEnabled((v) => !v), "Enabled", "Disabled")}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: "8px 0" }}>
          <span style={{ fontFamily: T.body, fontSize: "12px", color: T.t1 }}>Resume matching</span>
          {toggleBtn(resumeEnabled, () => setResumeEnabled((v) => !v), "Enabled", "Disabled")}
        </div>
      </div>

      {/* Disabled reason (only when something is off) */}
      {isDisabled && (
        <div style={{ background: T.s2, border: `1px solid ${T.b0}`,
          borderRadius: "8px", padding: "14px 16px", marginBottom: "12px" }}>
          <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
            letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "8px" }}>
            Disabled Reason
          </div>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Optional: reason for disabling matching"
            style={{
              width: "100%", boxSizing: "border-box",
              padding: "8px 10px", background: T.s1, border: `1px solid ${T.b1}`,
              borderRadius: "5px", color: T.t0, fontFamily: T.body, fontSize: "12px",
              outline: "none",
            }}
          />
        </div>
      )}

      {/* Admin notes */}
      <div style={{ background: T.s2, border: `1px solid ${T.b0}`,
        borderRadius: "8px", padding: "14px 16px", marginBottom: "12px" }}>
        <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
          letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "8px" }}>
          Admin Notes
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Internal notes about this user (not visible to user)"
          rows={4}
          style={{
            width: "100%", boxSizing: "border-box",
            padding: "8px 10px", background: T.s1, border: `1px solid ${T.b1}`,
            borderRadius: "5px", color: T.t0, fontFamily: T.body, fontSize: "12px",
            outline: "none", resize: "vertical",
          }}
        />
      </div>

      {/* Save button */}
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "12px" }}>
        {mut.isError && (
          <span style={{ fontFamily: T.body, fontSize: "11px", color: T.err }}>Save failed.</span>
        )}
        {mut.isSuccess && (
          <span style={{ fontFamily: T.mono, fontSize: "10px", color: T.ok }}>Saved</span>
        )}
        <button
          type="button"
          disabled={mut.isPending}
          onClick={() => mut.mutate()}
          style={{
            padding: "8px 18px", background: T.teal, border: "none",
            borderRadius: "6px", color: "#fff", fontFamily: T.body, fontSize: "12px",
            fontWeight: 600, cursor: mut.isPending ? "default" : "pointer",
            opacity: mut.isPending ? 0.6 : 1,
          }}
        >
          {mut.isPending ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

// ── Match Control Center ────────────────────────────────────────────────────

interface ActionAvail { enabled: boolean; reason?: string; costGated: boolean }
interface Readiness {
  state: string;
  recommendedNextAction: string;
  hasPartnerMapping: boolean;
  hasActiveResume: boolean;
  hasPreferences: boolean;
  matchCount: number;
  staleMatchCount: number;
  lastMatchedAt: string | null;
  avgScore: number | null;
  strongCount: number;
  jobPoolCount: number;
  previewMatchingEnabled: boolean;
  scoreWorkerExpected: boolean;
  quality: { stale: boolean; weak: boolean; ageDays: number | null };
  actions: {
    requestPreviewMatching: ActionAvail;
    rescoreCandidate: ActionAvail;
    refreshDiagnostics: ActionAvail;
  };
}

const STATE_LABEL: Record<string, string> = {
  no_partner_mapping: "No partner mapping",
  synced_no_resume: "Synced — no resume yet",
  resume_no_preferences: "Resume present — no preferences",
  ready_to_generate: "Ready to generate matches",
  matches_stale: "Matches are stale",
  matches_weak: "Matches look weak",
  matches_healthy: "Matches healthy",
};
const REASON_LABEL: Record<string, string> = {
  no_active_resume: "No active resume — add one before matching.",
  no_preferences: "No preferences on file — recommended before matching.",
  no_jobs: "No active jobs in the pool to match against.",
  no_matches_to_rescore: "No existing matches to rescore.",
  preview_matching_disabled: "Preview matching is disabled by a runtime flag.",
  worker_not_available: "The scoring worker is not enabled.",
  already_running: "A scoring run is already in progress.",
  cost_gate_required: "Confirm cost before running — uses embeddings/LLM credits.",
};
const ACTION_LABEL: Record<string, string> = {
  map_candidate: "Map candidate", add_resume: "Add a resume",
  add_preferences: "Add preferences", generate_preview_matches: "Generate preview matches",
  rescore_candidate: "Rescore against current jobs",
  improve_resume_or_preferences: "Improve resume / preferences", none: "No action needed",
};

function fmtWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toISOString().slice(0, 10);
}

function MatchControlTab({ userId }: { userId: string }) {
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();
  const [note, setNote] = useState<string>("");

  const q = useQuery<Readiness>({
    queryKey: ["admin-match-readiness", userId],
    queryFn: async () => {
      const { data } = await axios.get(`/api/admin/users/${userId}/match-readiness`);
      return data as Readiness;
    },
    enabled: !!token,
    retry: false,
  });

  const act = useMutation({
    mutationFn: async (action: "request_preview_matching" | "rescore" | "refresh_diagnostics") => {
      // Costly actions require an explicit cost confirmation. Refreshing is free.
      const costly = action !== "refresh_diagnostics";
      if (costly && !window.confirm("This may use embeddings/LLM credits. Continue?")) {
        throw new Error("cancelled");
      }
      const { data } = await axios.post(`/api/admin/users/${userId}/match-actions`, {
        action, ...(costly ? { confirmCost: true } : {}),
      });
      return data;
    },
    onSuccess: (data) => {
      if (data?.readiness) qc.setQueryData(["admin-match-readiness", userId], data.readiness);
      setNote(data?.enqueuedJobs != null ? `Queued (${data.enqueuedJobs} jobs).` : "Refreshed.");
    },
    onError: (err: unknown) => {
      const e = err as { message?: string; response?: { data?: { code?: string } } };
      if (e.message === "cancelled") return;
      const code = e.response?.data?.code;
      setNote(code ? (REASON_LABEL[code] ?? `Blocked: ${code}`) : "Action failed.");
    },
  });

  if (q.isLoading) return <p style={{ fontFamily: T.body, fontSize: "12px", color: T.t3 }}>Loading…</p>;
  if (q.isError || !q.data) return <p style={{ fontFamily: T.body, fontSize: "12px", color: T.err }}>Failed to load readiness.</p>;
  const r = q.data;

  const row = (label: string, value: React.ReactNode, ok?: boolean) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${T.b0}` }}>
      <span style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3, letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</span>
      <span style={{ fontFamily: T.body, fontSize: "12px", color: ok === undefined ? T.t1 : ok ? T.ok : T.warn, fontWeight: 600 }}>{value}</span>
    </div>
  );

  const actionBtn = (
    key: "request_preview_matching" | "rescore" | "refresh_diagnostics",
    label: string,
    a: ActionAvail,
  ) => (
    <div style={{ marginBottom: "8px" }}>
      <button
        type="button"
        disabled={!a.enabled || act.isPending}
        onClick={() => act.mutate(key)}
        style={{
          width: "100%", padding: "9px 12px", borderRadius: "6px", textAlign: "left",
          cursor: a.enabled ? "pointer" : "not-allowed",
          background: a.enabled ? T.tealBg : T.s3,
          border: `1px solid ${a.enabled ? T.tealBdr : T.b1}`,
          color: a.enabled ? T.tealSoft : T.t3,
          fontFamily: T.body, fontSize: "12px", fontWeight: 600,
        } as React.CSSProperties}
      >
        {label}{a.costGated ? "  ⚡" : ""}
      </button>
      {!a.enabled && a.reason && (
        <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3, marginTop: "3px" }}>
          {REASON_LABEL[a.reason] ?? a.reason}
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: "14px" }}>
        <div style={{ fontFamily: T.display, fontSize: "14px", fontWeight: 700, color: T.t0 }}>
          {STATE_LABEL[r.state] ?? r.state}
        </div>
        <div style={{ fontFamily: T.body, fontSize: "12px", color: T.t2, marginTop: "2px" }}>
          Recommended: {ACTION_LABEL[r.recommendedNextAction] ?? r.recommendedNextAction}
        </div>
      </div>

      {row("Resume", r.hasActiveResume ? "Active" : "Missing", r.hasActiveResume)}
      {row("Preferences", r.hasPreferences ? "Present" : "None", r.hasPreferences)}
      {row("Matches", r.matchCount + (r.staleMatchCount ? ` (+${r.staleMatchCount} stale)` : ""))}
      {row("Avg score", r.avgScore == null ? "—" : String(r.avgScore))}
      {row("Last scored", fmtWhen(r.lastMatchedAt))}
      {row("Job pool", r.jobPoolCount > 0 ? String(r.jobPoolCount) : "Empty", r.jobPoolCount > 0)}
      {row("Preview matching", r.previewMatchingEnabled ? "Enabled" : "Disabled (runtime)", r.previewMatchingEnabled)}
      {row("Score worker", r.scoreWorkerExpected ? "Enabled" : "Not available", r.scoreWorkerExpected)}

      <div style={{ marginTop: "16px" }}>
        <div style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: "8px" }}>
          Actions
        </div>
        {actionBtn("request_preview_matching", "Generate preview matches", r.actions.requestPreviewMatching)}
        {actionBtn("rescore", "Rescore against current jobs", r.actions.rescoreCandidate)}
        {actionBtn("refresh_diagnostics", "Refresh diagnostics (read-only)", r.actions.refreshDiagnostics)}
        <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3, marginTop: "4px" }}>
          ⚡ actions may use embeddings/LLM credits and stay disabled until runtime flags are enabled.
        </div>
        {note && <div style={{ fontFamily: T.body, fontSize: "11px", color: T.t2, marginTop: "8px" }}>{note}</div>}
      </div>
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────

type Tab = "profile" | "matches" | "applications" | "pool" | "match" | "controls";

export function UserDetail({ userId, onClose }: { userId: string; onClose: () => void }) {
  const [tab, setTab] = useState<Tab>("profile");
  const token = useAuthStore((s) => s.token);

  const q = useQuery<UserDetailData>({
    queryKey: ["admin-user-detail", userId],
    queryFn: async () => {
      const { data } = await axios.get(`/api/admin/users/${userId}`);
      return data;
    },
    enabled: !!token,
    retry: false,
  });

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 40 }}
      />

      {/* Panel */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(520px, 100vw)",
        background: T.s1, borderLeft: `1px solid ${T.b1}`,
        zIndex: 50, overflowY: "auto",
        display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{ padding: "18px 20px 0", borderBottom: `1px solid ${T.b0}`, flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "14px" }}>
            <div>
              {q.data && (
                <>
                  <div style={{ fontFamily: T.display, fontSize: "18px", fontWeight: 700, color: T.t0 }}>
                    {q.data.user.name}
                  </div>
                  <div style={{ fontFamily: T.mono, fontSize: "11px", color: T.t2, marginTop: "2px" }}>
                    {q.data.user.email}
                  </div>
                </>
              )}
              {q.isLoading && (
                <div style={{ fontFamily: T.body, fontSize: "13px", color: T.t3 }}>Loading…</div>
              )}
            </div>
            <button
              type="button" onClick={onClose}
              style={{ background: "none", border: "none", color: T.t2, fontSize: "20px",
                cursor: "pointer", lineHeight: 1, padding: "0 4px" }}
              aria-label="Close"
            >×</button>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", gap: 0 }}>
            {(["profile", "matches", "applications", "pool", "match", "controls"] as Tab[]).map((t) => (
              <button
                key={t} type="button"
                onClick={() => setTab(t)}
                style={{
                  padding: "7px 14px",
                  background: "none", border: "none",
                  borderBottom: `2px solid ${tab === t ? T.teal : "transparent"}`,
                  color: tab === t ? T.teal : T.t3,
                  fontFamily: T.mono, fontSize: "11px", letterSpacing: "0.08em",
                  textTransform: "capitalize", cursor: "pointer",
                  transition: "color 0.15s",
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "16px 20px", flex: 1 }}>
          {q.isError && (
            <p style={{ fontFamily: T.body, fontSize: "13px", color: T.err }}>Failed to load user details.</p>
          )}

          {q.data && tab === "profile"       && <ProfileTab data={q.data} />}
          {tab === "matches"                  && <MatchesTab userId={userId} />}
          {q.data && tab === "applications"   && (
            <ApplicationsTab userId={userId} summary={q.data.applicationSummary} />
          )}
          {tab === "pool"                     && <PoolFitTab userId={userId} />}
          {tab === "match"                    && <MatchControlTab userId={userId} />}
          {tab === "controls"                 && <ControlsTab userId={userId} />}
        </div>
      </div>
    </>
  );
}
